import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MoreHorizontal, Send, UserPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useI18n } from "../../i18n";
import {
  getMembers,
  inviteMember,
  Member,
  MemberInvitation,
  MembersOverview,
  removeMember,
  resendMemberInvitation,
  revokeMemberInvitation,
  setMemberRole,
} from "../../services/Members";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const nameOf = (m: Member) =>
  [m.firstName, m.lastName].filter(Boolean).join(" ") || m.email;

const errorText = (res: { errors?: Array<{ title?: string; detail?: string }> }, fallback: string) =>
  res.errors?.[0]?.detail || res.errors?.[0]?.title || fallback;

/** An organization's admins manage who can use Queri. */
const MembersPage: React.FC = () => {
  const { t } = useI18n();
  const m = t.members;
  const [data, setData] = useState<MembersOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Member | null>(null);

  const load = useCallback(async () => {
    const res = await getMembers();
    if (res.success && res.data) {
      setData(res.data);
      setForbidden(false);
    } else if (res.status === 403) {
      setForbidden(true);
    } else {
      toast.error(errorText(res, m.actionFailed));
    }
  }, [m.actionFailed]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const run = async (key: string, action: () => Promise<{ success: boolean; errors?: Array<{ title?: string; detail?: string }> }>, done: string) => {
    setBusy(key);
    try {
      const res = await action();
      if (res.success) {
        toast.success(done);
        await load();
        return true;
      }
      toast.error(errorText(res, m.actionFailed));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    const address = email.trim();
    if (!address) return;
    setInviting(true);
    try {
      const res = await inviteMember(address);
      if (res.success) {
        toast.success(m.invited(address));
        setEmail("");
        await load();
      } else {
        toast.error(errorText(res, m.inviteFailed));
      }
    } finally {
      setInviting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (forbidden || !data) {
    return (
      <div className="w-full p-8">
        <h1 className="text-3xl font-bold tracking-tight">{m.title}</h1>
        <p className="mt-2 text-muted-foreground">{m.noAccess}</p>
      </div>
    );
  }

  const seatsFull = data.seatLimit != null && data.seatsUsed >= data.seatLimit;

  return (
    <div className="w-full space-y-6 p-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{m.title}</h1>
          <p className="text-muted-foreground">{m.subtitle(data.organizationName)}</p>
        </div>
        <span className={`text-sm ${seatsFull ? "font-medium text-amber-700" : "text-muted-foreground"}`}>
          {data.seatLimit != null ? m.seats(data.seatsUsed, data.seatLimit) : m.seatsUnlimited(data.seatsUsed)}
        </span>
      </div>

      <Card className="p-4">
        <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => void invite(e)}>
          <div className="min-w-[260px] flex-1 space-y-1.5">
            <label htmlFor="invite-email" className="text-sm font-medium">
              {m.inviteLabel}
            </label>
            <Input
              id="invite-email"
              type="email"
              placeholder={m.invitePlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={inviting || !email.trim() || seatsFull}>
            {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            {inviting ? m.inviting : m.invite}
          </Button>
        </form>
        <p className="mt-2 text-xs text-muted-foreground">{m.inviteHint}</p>
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b p-4">
          <h2 className="text-base font-semibold">{m.people}</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{m.name}</TableHead>
              <TableHead>{m.role}</TableHead>
              <TableHead>{m.status}</TableHead>
              <TableHead>{m.joined}</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.members.map((member) => (
              <TableRow key={member.id}>
                <TableCell>
                  <div className="font-medium">
                    {nameOf(member)}
                    {member.isYou && <span className="ml-1 text-xs font-normal text-muted-foreground">({m.you})</span>}
                  </div>
                  {nameOf(member) !== member.email && (
                    <div className="text-xs text-muted-foreground">{member.email}</div>
                  )}
                </TableCell>
                <TableCell>
                  {member.role === "admin" ? (
                    <span className="rounded-full bg-brand/10 px-2 py-0.5 text-xs font-medium text-brand">{m.admin}</span>
                  ) : (
                    <span className="text-sm text-muted-foreground">{m.member}</span>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {member.status === "ACTIVE" ? m.active : <span className="text-muted-foreground">{m.invitedStatus}</span>}
                </TableCell>
                <TableCell className="text-sm">{formatDate(member.createdAt)}</TableCell>
                <TableCell>
                  {!member.isYou && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0" disabled={busy === member.id} aria-label={m.role}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() =>
                            void run(
                              member.id,
                              () => setMemberRole(member.id, member.role === "admin" ? "member" : "admin"),
                              m.roleChanged,
                            )
                          }
                        >
                          {member.role === "admin" ? m.makeMember : m.makeAdmin}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-600" onClick={() => setRemoving(member)}>
                          {m.remove}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {data.invitations.length > 0 && (
        <Card className="overflow-hidden">
          <div className="border-b p-4">
            <h2 className="text-base font-semibold">{m.pending}</h2>
          </div>
          <Table>
            <TableBody>
              {data.invitations.map((inv: MemberInvitation) => (
                <TableRow key={inv.id}>
                  <TableCell className="text-sm">{inv.email}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {m.sent} {formatDate(inv.createdAt)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {inv.expired ? m.expired : `${m.expires} ${formatDate(inv.expiresAt)}`}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy === inv.id}
                        onClick={() => void run(inv.id, () => resendMemberInvitation(inv.id), m.resent)}
                      >
                        <Send className="h-3 w-3" />
                        {m.resend}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy === inv.id}
                        onClick={() => void run(inv.id, () => revokeMemberInvitation(inv.id), m.revoked)}
                      >
                        <X className="h-3 w-3" />
                        {m.revoke}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <AlertDialog open={removing != null} onOpenChange={(open) => !open && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.removeTitle}</AlertDialogTitle>
            <AlertDialogDescription>{removing ? m.removeDescription(nameOf(removing)) : null}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{m.cancel}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => {
                e.preventDefault();
                const target = removing;
                if (!target) return;
                void run(target.id, () => removeMember(target.id), m.removed).then(() => setRemoving(null));
              }}
            >
              {m.remove}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MembersPage;
